import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  ageRangeChanged,
  maxPriceChanged,
  freeOnlyToggled,
  filtersReset,
  originCleared,
  originResolved,
  keywordToggled,
  radiusChanged,
  selectFilters,
} from './filtersSlice';
import { RADIUS_OPTIONS_MILES, UK_TOWNS } from './ukTowns';
import { useLocationOrigin } from './useLocationOrigin';
import { useSearchNavigation } from './searchUrl';
import { AGE_OPTIONS, ANY_AGE } from './ageOptions';
import { Callout } from '@mmef/shared/components';

/** Sentinel values for the two non-town options in the Where list. */
const ANYWHERE = '';
const MY_LOCATION = '__my_location__';

/**
 * One row for the question everyone asks — where? — and a disclosure for everything else.
 *
 * <p>Age and text filters used to sit in the same row, which left five controls fighting for
 * width and the age boxes overflowing behind the one next to them. Secondary filters now live
 * behind a toggle, which also leaves somewhere obvious to put the next one.
 *
 * <p>The toggle carries a count of what's active, so a filter that's hidden can't quietly shrink
 * someone's results with no visible explanation.
 */
interface Props {
  isSearching: boolean;
  /** Derived from the events currently found, so every chip returns something. */
  suggestions: string[];
}

export function EventFilters({ isSearching, suggestions }: Props) {
  const { origin, radiusMiles, keywords, ageFrom, ageTo, maxPrice, freeOnly } = useAppSelector(selectFilters);
  const dispatch = useAppDispatch();
  // Shared with the prompt above the results, so one permission flow rather than two.
  const { request: requestLocation, locating, error: locationError, clearError } = useLocationOrigin();
  const [showFilters, setShowFilters] = useState(false);
  const whereId = useId();
  const withinId = useId();
  const keywordsId = useId();
  const ageId = useId();
  const panelId = useId();

  const activeFilters =
    [ageFrom, ageTo, maxPrice].filter((v) => v !== '').length + keywords.length + (freeOnly ? 1 : 0);

  // The select is driven by state, so a failed geolocation falls back to "Anywhere" rather than
  // leaving the control showing a place we never resolved.
  const whereValue = !origin ? ANYWHERE : origin.source === 'geo' ? MY_LOCATION : origin.label;

  /*
   * A brief highlight when "Where" changes because of something else on the page — a city picked
   * from the list or the map, or Back. The search bar doesn't move during that change, so without it
   * the one control that did change is the easiest thing on the page to miss. Not when the reader
   * changed it themselves: they already know.
   */
  const changedHere = useRef(false);
  const previousWhere = useRef(whereValue);
  const [whereChanged, setWhereChanged] = useState(false);
  useEffect(() => {
    if (previousWhere.current === whereValue) return;
    previousWhere.current = whereValue;
    if (changedHere.current) {
      changedHere.current = false;
      return;
    }
    setWhereChanged(true);
    const timer = window.setTimeout(() => setWhereChanged(false), 700);
    return () => window.clearTimeout(timer);
  }, [whereValue]);

  const onWhereChange = (value: string) => {
    changedHere.current = true;
    clearError();
    if (value === ANYWHERE) {
      dispatch(originCleared());
      return;
    }
    if (value === MY_LOCATION) {
      requestLocation();
      return;
    }
    const town = UK_TOWNS.find((t) => t.name === value);
    if (town) {
      dispatch(originResolved({ lat: town.lat, lng: town.lng, label: town.name, source: 'town' }));
    }
  };

  const { search, backToOverview } = useSearchNavigation();

  // Into the address bar rather than straight into the store, so Back returns from the results.
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    search();
  };

  const onReset = () => {
    dispatch(filtersReset());
    backToOverview();
  };

  return (
    <form className="filters" onSubmit={onSubmit} aria-label="Find events">
      <div className="filters__bar">
        <div className="field">
          <label className="field__label" htmlFor={whereId}>
            Where
          </label>
          <select
            id={whereId}
            className={`field__input${whereChanged ? ' field__input--changed' : ''}`}
            value={whereValue}
            onChange={(e) => onWhereChange(e.target.value)}
          >
            <option value={ANYWHERE}>Anywhere in the UK</option>
            <option value={MY_LOCATION}>{locating ? 'Finding you…' : 'My location'}</option>
            {/* Somewhere picked on the homepage that isn't one of the towns below — New Malden,
                Walsall, anywhere else an organiser happens to use. Without an option for it the
                select has no value to show and goes blank, which reads as the search having
                forgotten where it is looking. */}
            {origin?.source === 'town' && !UK_TOWNS.some((t) => t.name === origin.label) && (
              <option value={origin.label}>{origin.label}</option>
            )}
            <optgroup label="Towns and cities">
              {UK_TOWNS.map((town) => (
                <option key={town.name} value={town.name}>
                  {town.name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* A radius is meaningless without somewhere to measure from, so it appears with one. */}
        {origin && (
          <div className="field field--narrow">
            <label className="field__label" htmlFor={withinId}>
              Within
            </label>
            <select
              id={withinId}
              className="field__input"
              value={radiusMiles}
              onChange={(e) => dispatch(radiusChanged(Number(e.target.value)))}
            >
              {RADIUS_OPTIONS_MILES.map((miles) => (
                <option key={miles} value={miles}>
                  {miles} miles
                </option>
              ))}
            </select>
          </div>
        )}

        <button className="button button--primary filters__submit" type="submit" disabled={isSearching}>
          {isSearching ? 'Searching…' : 'Search'}
        </button>
      </div>

      <div className="filters__meta">
        <button
          type="button"
          className="filters__toggle"
          aria-expanded={showFilters}
          aria-controls={panelId}
          onClick={() => setShowFilters((open) => !open)}
        >
          <span aria-hidden="true">{showFilters ? '▾' : '▸'}</span> Filters
          {activeFilters > 0 && <span className="filters__count">{activeFilters}</span>}
        </button>

        <button type="button" className="button button--link" onClick={onReset}>
          Reset
        </button>
      </div>

      {showFilters && (
        <div className="filters__panel" id={panelId}>
          <div className="field field--narrow">
            <label className="field__label" htmlFor={ageId}>
              For ages
            </label>
            {/* The ages the events should be for — not the searcher's own age. Dropdowns rather
                than number boxes: "any" becomes a choice instead of something you express by
                clearing a field. */}
            <div className="age-pair">
              <select
                id={ageId}
                className="field__input"
                aria-label="Events for ages from"
                value={ageFrom}
                onChange={(e) => dispatch(ageRangeChanged({ from: e.target.value, to: ageTo }))}
              >
                <option value={ANY_AGE}>Any</option>
                {AGE_OPTIONS.map((age) => (
                  <option key={age} value={age}>
                    {age}
                  </option>
                ))}
              </select>
              <span aria-hidden="true">–</span>
              <select
                className="field__input"
                aria-label="Events for ages to"
                value={ageTo}
                onChange={(e) => dispatch(ageRangeChanged({ from: ageFrom, to: e.target.value }))}
              >
                <option value={ANY_AGE}>Any</option>
                {AGE_OPTIONS.map((age) => (
                  <option key={age} value={age}>
                    {age}
                  </option>
                ))}
              </select>
            </div>
            <p className="field__hint">
              Plenty of events don&apos;t mention an age range. We&apos;ll still show you those,
              grouped at the end.
            </p>
          </div>

          <div className="field">
            <span className="field__label">Price</span>
            <div className="age-pair">
              <input
                className="field__input"
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                aria-label="Maximum ticket price in pounds"
                placeholder="No maximum"
                value={maxPrice}
                disabled={freeOnly}
                onChange={(e) => dispatch(maxPriceChanged(e.target.value))}
              />
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={freeOnly}
                  onChange={(e) => dispatch(freeOnlyToggled(e.target.checked))}
                />{' '}
                Free events only
              </label>
            </div>
            {/* Said plainly, because it's the surprising part: most listings are imported and carry
                no price, and silently dropping them would empty the page rather than narrow it. */}
            <p className="field__hint">
              Events that don&apos;t state a price are always shown — we can&apos;t rule out a
              listing over a number nobody gave us.
            </p>
          </div>

          {/* Only when the results actually share some vocabulary. An empty filter is worse than
              no filter — it implies you could narrow things and then refuses to. */}
          {suggestions.length > 0 && (
            <div className="field">
              <span className="field__label" id={keywordsId}>
                Narrow it down
              </span>
              <div className="chips" role="group" aria-labelledby={keywordsId}>
                {suggestions.map((keyword) => {
                  const selected = keywords.includes(keyword);
                  return (
                    <button
                      key={keyword}
                      type="button"
                      className={`chip${selected ? ' chip--on' : ''}`}
                      aria-pressed={selected}
                      onClick={() => dispatch(keywordToggled(keyword))}
                    >
                      {keyword}
                    </button>
                  );
                })}
              </div>
              <p className="field__hint">
                Taken from the events found, so every one of these returns something.
              </p>
            </div>
          )}
        </div>
      )}

      <p className="field__hint filters__summary">
        {origin ? (
          <>
            Showing events within {radiusMiles} miles of <strong>{origin.label}</strong>.
            {/*
              Where a shared location actually landed, phrased as a note about the search rather
              than an announcement about the person. "Measuring from around X" is a fact about what
              this page is doing; "your location is X" is the site telling somebody where they are,
              which is unpleasant even when it's right — and browsers are often a few miles out.
              The "pick a town" out is what makes it useful rather than merely informative.
            */}
            {origin.source === 'geo' && origin.place && (
              <>
                {' '}
                Measuring from around <strong>{origin.place}</strong> — if that&apos;s off, pick a
                town above instead.
              </>
            )}
          </>
        ) : (
          <>Showing events across the UK. Pick a place to search nearby.</>
        )}
      </p>

      {locationError && <Callout tone="error">{locationError}</Callout>}
    </form>
  );
}
